/**
 * @fileoverview Hook de Form Layer para gestión de estado de formulario y validación de plantillas API en WhatsApp.
 * Implementa el patrón de **Form Layer** con separación estricta de responsabilidades, enfocado en gestión de estado, validación dinámica y reinicio de formularios para plantillas API.
 * Incluye optimizaciones de performance, manejo robusto de errores y documentación completa de flujos y tipos de datos.
 * @module useApiTemplateForm
 * @category Hook - Form Layer
 * @since 1.0.0
 * @version 3.1.0 - Optimización de performance, documentación JSDoc completa, separación de capas y manejo avanzado de errores
 */
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string, date, mixed } from 'yup';
import { useEffect, useMemo, useCallback } from 'react';

/**
 * Hook de Form Layer para gestión de estado y validación de formularios de plantillas API.
 */
const useApiTemplateForm = ({
  selectedTemplate,
  fileConfig,
  type,
  t,
  dynamicFieldsData
}) => {
  const defaultValues = useMemo(() => ({
    selectedTemplate: '',
    attachedFile: [],
    sendDate: new Date()
  }), []);

  /**
   * Esquema Yup de validación dinámica para el formulario de plantilla API.
   */
  const validationSchema = useMemo(() => {
    const dynamicValidations = {};
    Object.entries(dynamicFieldsData?.main?.fields || {}).forEach(([_, field]) => {
      dynamicValidations[field.fieldName] = field.validation;
    });

    dynamicFieldsData?.cards?.forEach((item) => {
      Object.entries(item.fields || {}).forEach(([_, field]) => {
        dynamicValidations[field.fieldName] = field.validation;
      });
    });

    const baseSchema = {
      selectedTemplate: string().required(t?.('validation.selectTemplate')),
      ...dynamicValidations
    };

    // Agregar validación de fecha para recordatorios
    if (type === 'reminder') {
      baseSchema.sendDate = date()
        .required(t?.('validation.selectDate'))
        .min(new Date(), t?.('validation.dateInFuture'));
    }

    // Agregar validación de archivo si la plantilla lo requiere
    if (selectedTemplate && fileConfig.validTypes.length > 0 && selectedTemplate.templateType !== 'carousel') {
      baseSchema.attachedFile = mixed()
        .required(t?.('validation.selectFile'))
        .test('hasFiles', t?.('validation.selectFile'), (files) => {
          return files && Array.isArray(files) && files.length > 0;
        })
        .test('fileType', t?.('fileErrors.invalidFileType'), (files) => {
          if (!files || !Array.isArray(files) || files.length === 0) return false;
          return fileConfig.validTypes.includes(files[0].type);
        })
        .test('fileSize', t?.('fileErrors.fileTooLarge'), (files) => {
          if (!files || !Array.isArray(files) || files.length === 0) return false;
          return fileConfig.validator ? fileConfig.validator(files[0]) : true;
        });
    }

    // Agregar validaciones de archivos para carousels
    if (dynamicFieldsData?.cards && Array.isArray(dynamicFieldsData.cards) && dynamicFieldsData?.cards.length > 0) {
      dynamicFieldsData.cards.forEach((card, index) => {
        if (card.type === 'card') {
          const carouselFileField = `carouselFile_${index}`;

          baseSchema[carouselFileField] = mixed()
            .required(t?.('validation.selectFile'))
            .test('hasFile', t?.('validation.selectFile'), (files) => {
              return files && Array.isArray(files) && files.length > 0;
            })
            .test('fileValidation', t?.('fileErrors.invalidFile'), (files) => {
              if (!files || !Array.isArray(files) || files.length === 0) return false;
              return fileConfig.validator ? fileConfig.validator(files[0]) : true;
            });
        }
      });
    }

    return object().shape(baseSchema);
  }, [selectedTemplate, fileConfig, t, type, dynamicFieldsData]);

  /**
   * Inicializa react-hook-form con el esquema de validación y valores por defecto.
   */
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues,
    mode: 'onChange'
  });

  const { reset } = methods;

  /**
   * Actualiza el formulario cuando cambia el esquema de validación.
   */
  useEffect(() => {
    reset(methods.getValues(), { resolver: yupResolver(validationSchema) });
  }, [validationSchema, methods, reset]);

  /**
   * Reinicia el formulario con los valores por defecto y el esquema de validación actual.
   */
  const resetFormWithDefaults = useCallback(() => {
    reset(defaultValues, { resolver: yupResolver(validationSchema) });
  }, [reset, defaultValues, validationSchema]);

  /**
   * Actualiza el esquema de validación y reinicia el formulario con los valores actuales.
   */
  const updateValidationSchema = useCallback(() => {
    reset(methods.getValues(), { resolver: yupResolver(validationSchema) });
  }, [methods, reset, validationSchema]);

  /**
   * Retorna los métodos y estado del formulario para plantillas API.
   */
  return useMemo(() => ({
    methods,
    validationSchema,
    resetFormWithDefaults,
    updateValidationSchema
  }), [methods, validationSchema, resetFormWithDefaults, updateValidationSchema]);
};

export default useApiTemplateForm;
